import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FolderPlus, Plus, Trash2 } from 'lucide-react';
import { PERSONAL_DESTINATION, teamDestinationValue } from '../utils/vaultDestinations';

/**
 * The vault sidebar's quick-create menu — shown both by the small "+" on a
 * heading and by a right-click on the same row, so the two routes can never
 * drift apart. Same recipe as the app's other context menus (ChatRowContextMenu,
 * FtpSidebar's RowMenu): portaled to `document.body` so the sidebar splitter's
 * stacking context cannot clip it, `fixed` at the cursor or under the button,
 * dismissed by an outside mousedown, Escape, or a window blur.
 *
 * A TARGET is what the menu was opened on, and is the whole input:
 *
 *   { kind: 'personal' }                                  the PERSONAL heading
 *   { kind: 'personalFolder', folder }                    a personal folder row
 *   { kind: 'team', teamId, canWrite }                    a team heading
 *   { kind: 'teamFolder', teamId, folderId, canWrite }    a team folder row
 *                                                         (folderId '' = root)
 *
 * `canWrite` is decided by the caller from `buildDestinations` /
 * `canWriteTeamFolder` — this file never reads a permission field itself, so
 * there is exactly one notion of writability in the app.
 */

/**
 * Which items a target gets, and whether each is offered or refused.
 *
 * Pure and exported because it IS the behavioural surface: "does a read-only
 * team still show New item, and is it disabled with a reason" is the question
 * worth a test, and it is unanswerable while the answer lives inside JSX.
 *
 * A team the user cannot write to keeps the item, DISABLED, with the reason as
 * its tooltip. Hiding it would teach the user that the feature does not exist;
 * the missing thing is write access, and the fix is to go ask an admin — the
 * same reasoning the empty-team messages next to it already follow. Enabling it
 * would be worse still: a whole credential form filled in and then refused.
 *
 * DELETE IS OFFERED; RENAME IS NOT. Both were held back at first for the same
 * reason — a personal folder is DERIVED from a string inside each encrypted
 * item body, so neither is a one-row edit. Delete earned its way in because the
 * question it has to answer has a SAFE default: the folder goes, the items come
 * out of it and stay. Rename still has no answer that is obviously right when
 * two devices disagree mid-rename, so it waits.
 *
 * Delete is marked `destructive` so the menu can style it apart. It never
 * destroys credentials on its own — that needs a checkbox and the master
 * password in the confirmation dialog (VaultFolderDeleteModal).
 */
export function vaultSidebarMenuItems(target, t) {
  switch (target?.kind) {
    case 'personal':
      return [
        { id: 'new-item', label: t('vault.newItem'), Icon: Plus },
        { id: 'new-folder', label: t('vault.newFolder'), Icon: FolderPlus },
      ];
    case 'personalFolder':
      return [
        { id: 'new-item', label: t('vault.newItem'), Icon: Plus },
        { id: 'delete-folder', label: t('vault.deleteFolder'), Icon: Trash2, destructive: true },
      ];
    case 'team':
      // Same two actions as Personal, for the same reason: a heading stands for
      // a whole vault, and "add something here" is ambiguous between an item and
      // a folder. Folder rows keep a single-action "+" because there is only one
      // sensible thing to add inside a folder.
      return [
        {
          id: 'new-item',
          label: t('vault.newItem'),
          Icon: Plus,
          disabled: !target.canWrite,
          title: target.canWrite ? undefined : t('vault.noTeamWriteAccess'),
        },
        // A STRICTER grant than the item above. Creating a folder needs full
        // write scope on the server (`require_full_write`) — a member with a
        // grant on one folder may put credentials in it but may not reshape the
        // team's vault around it. Gating both on `canWrite` gave that member an
        // enabled control and a 403. `canManageFolders` is reported by the
        // server; absent (an older payload) it is false, which refuses rather
        // than promises.
        {
          id: 'new-folder',
          label: t('vault.newFolder'),
          Icon: FolderPlus,
          disabled: !target.canManageFolders,
          title: target.canManageFolders ? undefined : t('vault.noTeamFolderManageAccess'),
        },
      ];
    case 'teamFolder':
      // The ROOT row is not a folder — it is "no folder". There is nothing to
      // delete, and offering it would read as "delete everything unfiled".
      if (target.folderId === '' || target.folderId == null) {
        return [
          {
            id: 'new-item',
            label: t('vault.newItem'),
            Icon: Plus,
            disabled: !target.canWrite,
            title: target.canWrite ? undefined : t('vault.noFolderWriteAccess'),
          },
        ];
      }
      return [
        {
          id: 'new-item',
          label: t('vault.newItem'),
          Icon: Plus,
          disabled: !target.canWrite,
          title: target.canWrite ? undefined : t('vault.noFolderWriteAccess'),
        },
        // Deleting a team folder needs FULL write scope, not the per-folder
        // write above — `require_full_write` on the server. A member who may
        // add credentials to this folder may not remove the folder itself.
        {
          id: 'delete-folder',
          label: t('vault.deleteFolder'),
          Icon: Trash2,
          destructive: true,
          disabled: !target.canManageFolders,
          title: target.canManageFolders ? undefined : t('vault.noTeamFolderManageAccess'),
        },
      ];
    default:
      return [];
  }
}

/**
 * The `vault-new-item` detail for a target — where the modal should open
 * pre-filed.
 *
 * Returns null for a target that cannot be written to, so a seed can never
 * describe a save the server would reject even if a caller forgets to check.
 *
 * `folder` is a personal folder NAME (personal folders are a name in the
 * encrypted body); `teamFolderId` is a server row id, or '' for the team root.
 * The two are never mixed — writing an id into `folder` would file a personal
 * item under a meaningless number.
 */
export function newItemSeedFor(target) {
  switch (target?.kind) {
    case 'personal':
      return { destination: PERSONAL_DESTINATION, folder: null, teamFolderId: null };
    case 'personalFolder':
      return { destination: PERSONAL_DESTINATION, folder: target.folder || null, teamFolderId: null };
    case 'team':
      if (!target.canWrite) return null;
      // No folder: the team heading means "somewhere in this team", and the
      // modal picks the root or the first folder the user may write to.
      return { destination: teamDestinationValue(target.teamId), folder: null, teamFolderId: null };
    case 'teamFolder':
      if (!target.canWrite) return null;
      return {
        destination: teamDestinationValue(target.teamId),
        folder: null,
        teamFolderId: target.folderId == null ? '' : String(target.folderId),
      };
    default:
      return null;
  }
}

export default function VaultSidebarMenu({ x, y, target, t, onSelect, onClose }) {
  const menuRef = useRef(null);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('blur', onClose);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose]);

  const items = vaultSidebarMenuItems(target, t);
  if (!items.length || typeof document === 'undefined' || !document.body) return null;

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      role="menu"
      data-testid="vault-sidebar-menu"
      className="fixed z-50 min-w-40 animate-fadeIn rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
      style={{ left: x, top: y }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map(({ id, label, Icon, disabled, title, destructive }) => (
        <button
          key={id}
          type="button"
          role="menuitem"
          // A destructive entry reads red, so it is not one careless click away
          // from the harmless ones directly above it.
          className={`ctx-menu-btn${destructive ? ' ctx-menu-btn--danger' : ''}`}
          disabled={!!disabled}
          title={title}
          onClick={() => {
            if (disabled) return;
            onClose();
            onSelect(id);
          }}
        >
          <Icon size={14} />
          <span>{label}</span>
        </button>
      ))}
    </div>,
    document.body
  );
}
